import { useEffect, useRef } from "react";

/**
 * Add a global Escape-key listener that calls `onClose`. Cleans up on
 * unmount. Use inside any popover/sheet/dialog component so keyboard
 * users can dismiss with a single press.
 */
export function useEscapeToClose(onClose) {
  useEffect(() => {
    if (typeof onClose !== "function") return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);
}

/**
 * Focus the referenced element once when the dialog is open. Pass the
 * dialog's `open` boolean so the effect re-runs whenever the dialog
 * actually appears — not just once on first mount (which would miss
 * dialogs whose body is conditionally rendered after a state flip).
 *
 * Returns a ref to attach to the element that should receive focus.
 */
export function useAutoFocusOnOpen(open = true) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    // Defer one tick so the element is actually in the DOM + visible
    // before we move focus (avoids losing focus to a render race).
    const id = window.setTimeout(() => {
      try {
        el.focus({ preventScroll: false });
      } catch (_) {
        /* element may not be focusable yet */
      }
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);
  return ref;
}
